import { useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Factory,
  ListTree,
  Loader2,
  Mail,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { OrganizationType } from "~/api/model";
import { useCreateOrganization } from "~/api/organizations/organizations";
import { getGetCurrentUserQueryKey } from "~/api/users/users";
import companyNameBg from "~/assets/onboarding/company-name.svg";
import createAccountBg from "~/assets/onboarding/create-account.svg";
import pipelineModeBg from "~/assets/onboarding/pipeline-mode.svg";
import successBg from "~/assets/onboarding/success.svg";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SKY_ACCOUNT_CREATED } from "~/constants/index";
import {
  isCustomEmailDomain,
  removeAllTokens,
  ROUTES,
  validateEmail,
} from "~/helpers";
import { useTrackAccountCreation } from "~/hooks/useTrackAccountCreation";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { useAuthStepImage } from "../hooks/useAuthStepImage";
import { PipelineModeCard } from "./PipelineModeCard";
import { RegisterDemoWidget } from "./RegisterDemoWidget";
import { TermsAndPolicyDialog } from "./TermsAndPolicyDialog";

enum REGISTER_STEP {
  PERSONAL_DETAILS = 1,
  COMPANY_DETAILS_NAME = 2,
  COMPANY_DETAILS_TYPE = 3,
  PIPELINE_MODE = 4,
  SUCCESS = 5,
  DEMO_REQUEST = 6,
}

type PipelineMode = "USE_TOC" | "USE_SNOWFLAKE" | "USE_SMART_AGENT";

interface FormErrors {
  email?: boolean;
  fullname?: boolean;
  companyName?: boolean;
  companyType?: boolean;
  terms?: boolean;
  duplicateEmail?: boolean;
}

const COMPANY_TYPES = [
  {
    value: OrganizationType.IME,
    label: "Insurance",
    description: "Policies, claims processing, and risk assessments",
    tags: [
      "Ambulance Call Reports",
      "Assessor Reports",
      "CAT Reports",
      "Clinic Notes",
      "Consultation Reports",
    ],
  },
  {
    value: OrganizationType.LOGISTICS,
    label: "Logistics",
    description:
      "Supply chain management, shipping manifests, and inventory records",
    tags: [
      "Ambulance Reports",
      "Anaesthetic Record",
      "CT Scans",
      "Assessment Form",
      "Electrocardiogram",
    ],
  },
  {
    value: OrganizationType.LEGAL,
    label: "Legal",
    description:
      "Law firms, contracts, litigation documents, and court filings",
    tags: [
      "Form 1",
      "Job Site",
      "Legal Correspondence",
      "OCF-18",
      "OCF 1 Form",
    ],
  },
  {
    value: OrganizationType.FINANCE,
    label: "Finance",
    description: "Banking, auditing, tax filings, and investment reports",
    tags: [
      "Form 1",
      "Job Site",
      "Legal Correspondence",
      "OCF-18",
      "OCF 1 Form",
    ],
  },
  {
    value: OrganizationType.HOSPITALITY,
    label: "Hospitality",
    description: "Patient records, medical charts, and hospital administration",
    tags: [
      "Ambulance Call Reports",
      "Assessor Reports",
      "CAT Reports",
      "Clinic Notes",
      "Consultation Reports",
    ],
  },
  {
    value: OrganizationType.ENERGY,
    label: "Energy",
    description: "Oil, gas, utilities, and infrastructure maintenance reports",
    tags: [
      "Ambulance Reports",
      "Anaesthetic Record",
      "CT Scans",
      "Assessment Form",
      "Electrocardiogram",
    ],
  },
  {
    value: OrganizationType.COMPENSATION,
    label: "Workers' Compensation",
    description:
      "Employee injury claims, medical reports, and benefits administration",
    tags: [
      "Form 1",
      "Job Site",
      "Legal Correspondence",
      "OCF-18",
      "OCF 1 Form",
    ],
  },
  {
    value: OrganizationType.CONSTRUCTIONS,
    label: "Construction",
    description: "Building projects, permits, and safety documentation",
    tags: ["Site Reports", "Safety Forms", "Permits", "Inspections"],
  },
  {
    value: OrganizationType.ADVERTISING,
    label: "Advertising",
    description: "Marketing campaigns, media planning, and creative assets",
    tags: ["Campaign Reports", "Media Plans", "Creative Briefs"],
  },
  {
    value: OrganizationType.MANUFACTURING,
    label: "Manufacturing",
    description:
      "Production records, quality control, and inventory management",
    tags: ["Production Reports", "QC Forms", "Inventory Records"],
  },
  {
    value: OrganizationType.ZONING,
    label: "Land Assessment and Zoning",
    description: "Property evaluations, zoning permits, and land surveys",
    tags: ["Survey Reports", "Zoning Permits", "Property Assessments"],
  },
  {
    value: OrganizationType.HEALTHCARE,
    label: "Long Term and Rehabilitation Care",
    description:
      "Patient care plans, rehabilitation progress, and medical records",
    tags: ["Care Plans", "Progress Notes", "Medical Records"],
  },
  {
    value: OrganizationType.ALLIED_HEALTH,
    label: "Allied Health Professionals",
    description: "Therapy reports, diagnostic assessments, and treatment plans",
    tags: ["Therapy Reports", "Assessments", "Treatment Plans"],
  },
  {
    value: OrganizationType.CLINICAL_SPECIALISTS,
    label: "Clinical Specialists",
    description:
      "Specialist consultations, diagnostic reports, and clinical notes",
    tags: ["Consultation Reports", "Diagnostic Reports", "Clinical Notes"],
  },
  {
    value: OrganizationType.LEGAL_HEALTH,
    label: "Legal and Health Professionals",
    description:
      "Medical-legal reports, expert opinions, and case documentation",
    tags: ["Medical-Legal Reports", "Expert Opinions", "Case Files"],
  },
  {
    value: OrganizationType.UNDERWRITING,
    label: "Underwriting",
    description: "Risk assessment, policy evaluation, and financial analysis",
    tags: ["Risk Reports", "Policy Evaluations", "Financial Analysis"],
  },
];

export const RegisterForm = () => {
  const intl = useIntl();
  const [email, setEmail] = useState("");
  const [fullname, setFullname] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState<OrganizationType | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [currentStep, setCurrentStep] = useState<REGISTER_STEP>(
    REGISTER_STEP.PERSONAL_DETAILS
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pipelineMode, setPipelineMode] =
    useState<PipelineMode>("USE_SMART_AGENT");
  const { mutate, isPending } = useCreateOrganization();
  const { trackCreateAccount } = useTrackAccountCreation();
  const queryClient = useQueryClient();

  const stepImage = useMemo(() => {
    switch (currentStep) {
      case REGISTER_STEP.PERSONAL_DETAILS:
        return createAccountBg;
      case REGISTER_STEP.COMPANY_DETAILS_NAME:
        return companyNameBg;
      case REGISTER_STEP.COMPANY_DETAILS_TYPE:
        return companyNameBg;
      case REGISTER_STEP.PIPELINE_MODE:
        return pipelineModeBg;
      case REGISTER_STEP.SUCCESS:
        return successBg;
      case REGISTER_STEP.DEMO_REQUEST:
        return undefined;
      default:
        return createAccountBg;
    }
  }, [currentStep]);

  useAuthStepImage(stepImage);

  const validateFirstStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!validateEmail(email)) {
      newErrors.email = true;
      isValid = false;
    } else if (!isCustomEmailDomain(email)) {
      // Temporary: Show inline Calendly for public/free email domains to test lead quality
      setCurrentStep(REGISTER_STEP.DEMO_REQUEST);
      return false;
    }

    if (!fullname.trim()) {
      newErrors.fullname = true;
      isValid = false;
    }

    if (!termsAccepted) {
      newErrors.terms = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateSecondStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyName.trim()) {
      newErrors.companyName = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateSecondDetailsStep = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyType) {
      newErrors.companyType = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleContinue = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    removeAllTokens();
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    queryClient.invalidateQueries();

    if (currentStep === REGISTER_STEP.PERSONAL_DETAILS) {
      if (validateFirstStep()) {
        setCurrentStep(REGISTER_STEP.COMPANY_DETAILS_NAME);
      }
    } else if (currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME) {
      if (validateSecondStep()) {
        setCurrentStep(REGISTER_STEP.COMPANY_DETAILS_TYPE);
      }
    } else if (currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE) {
      if (validateSecondDetailsStep()) {
        setCurrentStep(REGISTER_STEP.PIPELINE_MODE);
      }
    } else if (currentStep === REGISTER_STEP.PIPELINE_MODE) {
      trackEvent({
        eventName: "app/register/register.button/submit",
        properties: {
          email,
          fullname,
          companyName,
          companyType,
          pipelineMode,
        },
      });
      mutate(
        {
          data: {
            ownerEmail: email,
            ownerName: fullname,
            orgName: companyName,
            orgType: companyType ?? OrganizationType.IME,
            pipelineMode,
          } as Parameters<typeof mutate>[0]["data"] & {
            pipelineMode: PipelineMode;
          },
        },
        {
          onSuccess: () => {
            trackCreateAccount();
            localStorage.setItem(SKY_ACCOUNT_CREATED, "true");
            setCurrentStep(REGISTER_STEP.SUCCESS);
          },
          onError: (error: unknown) => {
            const axiosError = error as {
              response?: { status?: number; data?: { code?: number } };
            };
            const httpStatus = axiosError?.response?.status;
            const bodyCode = axiosError?.response?.data?.code;
            const isUserAlreadyExists = httpStatus === 409 || bodyCode === 409;

            if (isUserAlreadyExists) {
              setErrors((prev) => ({ ...prev, duplicateEmail: true }));
              setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS);
            } else {
              logger.error("Registration error:", error);
              setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS);
            }
          },
        }
      );
    }
  };

  return (
    <div className="flex justify-center items-start pt-10">
      <div
        className={`flex flex-col gap-6 w-full ${
          currentStep === REGISTER_STEP.DEMO_REQUEST
            ? "max-w-4xl"
            : "max-w-[560px]"
        }`}
      >
        {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
          <UsersRound className="h-[32px] w-[32px] text-[#71717A]" />
        ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ||
          currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
          <Factory className="h-[32px] w-[32px] text-[#71717A]" />
        ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
          <Sparkles className="h-[32px] w-[32px] text-[#71717A]" />
        ) : currentStep === REGISTER_STEP.SUCCESS ? (
          <Mail className="h-[32px] w-[32px] text-[#71717A]" />
        ) : (
          <UsersRound className="h-[32px] w-[32px] text-[#71717A]" />
        )}
        <div>
          <h3 className="text-2xl font-semibold text-base-foreground pt-[16px]">
            {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
              <FormattedMessage
                id="auth.register.title"
                defaultMessage="Create Account"
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
              <FormattedMessage
                id="auth.register.companyDetails"
                defaultMessage="Company"
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
              <FormattedMessage
                id="auth.register.companyDetailsType"
                defaultMessage="Industry"
              />
            ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
              <FormattedMessage
                id="auth.register.pipelineMode"
                defaultMessage="Document Processing Mode"
              />
            ) : currentStep === REGISTER_STEP.SUCCESS ? (
              <FormattedMessage
                id="auth.register.checkEmail"
                defaultMessage="Check Your Email"
              />
            ) : (
              <FormattedMessage
                id="auth.register.demoRequest"
                defaultMessage="Request a Demo"
              />
            )}
          </h3>
          <p className="text-sm text-[#71717A] mt-[16px]">
            {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
              <FormattedMessage
                id="auth.register.subtitle"
                defaultMessage="Please enter your email address and full name to create a Sky AI account."
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
              <FormattedMessage
                id="auth.register.companyDetailsSubtitle"
                defaultMessage="Please provide your company name."
              />
            ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
              <FormattedMessage
                id="auth.register.companyDetailsTypeSubtitle"
                defaultMessage="What kinds of documents do you usually deal with at work?"
              />
            ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
              <FormattedMessage
                id="auth.register.pipelineModeDescription"
                defaultMessage="Choose how Sky AI should process your documents"
              />
            ) : currentStep === REGISTER_STEP.SUCCESS ? (
              <FormattedMessage
                id="auth.register.verificationEmail"
                defaultMessage="We've emailed you a temporary password and registration link. If not received within an hour, check spam or contact support."
                values={{
                  support: (chunks) => (
                    <a
                      href="mailto:support@usesky.ai"
                      className="text-blue-600 hover:underline"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                id="auth.register.demoRequest"
                defaultMessage="Request a Demo"
              />
            )}
          </p>
        </div>
        <form
          onSubmit={handleContinue}
          className="w-full flex flex-col gap-[24px] text-sm"
        >
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <>
              <div className="w-full flex flex-col gap-[16px]">
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="registerEmail"
                    className="block font-medium text-base-foreground"
                  >
                    <FormattedMessage
                      id="auth.register.email"
                      defaultMessage="Email"
                    />
                  </Label>
                  <Input
                    id="registerEmail"
                    placeholder={intl.formatMessage({
                      id: "auth.register.emailPlaceholder",
                      defaultMessage: "Enter your email",
                    })}
                    value={email}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
                      errors.email ? "border-red-600" : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                  />
                  {errors.email && (
                    <span className="text-red-600">
                      <FormattedMessage
                        id="auth.register.emailError"
                        defaultMessage="Email should be <strong>example@site.com</strong>"
                        values={{
                          strong: (chunks) => (
                            <strong className="font-semibold">{chunks}</strong>
                          ),
                        }}
                      />
                    </span>
                  )}
                  {errors.duplicateEmail && (
                    <span className="text-red-600">
                      <FormattedMessage
                        id="auth.register.duplicateEmailError"
                        defaultMessage="An account with this email already exists"
                      />
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="registerFullname"
                    className="block font-medium text-base-foreground"
                  >
                    <FormattedMessage
                      id="auth.register.fullname"
                      defaultMessage="Full name"
                    />
                  </Label>
                  <Input
                    id="registerFullname"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "auth.register.fullnamePlaceholder",
                      defaultMessage: "Enter your full name",
                    })}
                    value={fullname}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-none ${
                      errors.fullname ? "border-red-600" : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setFullname(e.target.value);
                    }}
                  />
                  {errors.fullname && (
                    <span className="text-sm text-red-600">
                      <FormattedMessage
                        id="auth.register.fullnameError"
                        defaultMessage="This field cannot be empty"
                      />
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start space-x-2 py-2">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => {
                        setTermsAccepted(checked as boolean);
                      }}
                      className={`cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border border-[#E4E4E7] shadow-none ${errors.terms ? "border-red-600" : ""}`}
                    />
                    <div className="flex flex-col gap-1">
                      <Label className="font-[400] text-base-foreground cursor-default">
                        <FormattedMessage
                          id="auth.register.termsAgreement"
                          defaultMessage="Agree to the {terms} and {privacy}"
                          values={{
                            terms: (
                              <TermsAndPolicyDialog type="terms">
                                <span className="text-blue-600 cursor-pointer">
                                  <FormattedMessage
                                    id="auth.register.terms"
                                    defaultMessage="Terms and Conditions"
                                  />
                                </span>
                              </TermsAndPolicyDialog>
                            ),
                            privacy: (
                              <TermsAndPolicyDialog type="policy">
                                <span className="text-blue-600 cursor-pointer">
                                  <FormattedMessage
                                    id="auth.register.privacy"
                                    defaultMessage="Privacy Policy"
                                  />
                                </span>
                              </TermsAndPolicyDialog>
                            ),
                          }}
                        />
                      </Label>
                    </div>
                  </div>
                  {errors.terms && (
                    <span className="text-red-600">
                      <FormattedMessage
                        id="auth.register.termsError"
                        defaultMessage="You must agree to the Terms and Conditions and Privacy Policy"
                      />
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
            <>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="companyName"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.companyName"
                    defaultMessage="Company name"
                  />
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "auth.register.companyNamePlaceholder",
                    defaultMessage: "Enter your company name",
                  })}
                  value={companyName}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.companyName ? "border-red-600" : "border-gray-300"
                  }`}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                  }}
                />
                {errors.companyName && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyNameError"
                      defaultMessage="Company name cannot be empty"
                    />
                  </span>
                )}
              </div>
            </>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
            <>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto p-1">
                  {COMPANY_TYPES.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => setCompanyType(type.value)}
                      className={`
                        relative flex cursor-pointer rounded-lg border p-4 transition-all
                        ${
                          companyType === type.value
                            ? "border-[#60A5FA] ring-1 ring-[#60A5FA] bg-sky-50"
                            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white"
                        }
                      `}
                    >
                      <div className="flex flex-1 gap-4">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="block text-sm font-[500] text-[#18181B]">
                            <FormattedMessage
                              id={`auth.register.companyType.${type.value}`}
                              defaultMessage={type.label}
                            />
                          </span>
                          <span className="mt-[6px] text-xs text-[#71717A]">
                            <FormattedMessage
                              id={`auth.register.companyType.${type.value}.description`}
                              defaultMessage={type.description}
                            />
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-y-[6px] gap-x-[4px] justify-start items-start max-w-[256px]">
                          {type.tags.slice(0, 5).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-full bg-[#F4F4F5] px-2 py-[2px] text-xs text-[#18181B]"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.companyType && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyTypeError"
                      defaultMessage="Please select the type of company"
                    />
                  </span>
                )}
              </div>
            </>
          ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
            <div className="flex flex-col gap-4">
              <div className="space-y-[8px]">
                <PipelineModeCard
                  mode="USE_SMART_AGENT"
                  currentMode={pipelineMode}
                  onSelect={setPipelineMode}
                  icon={Bot}
                  iconColor="text-[#2563EB]"
                  titleId="auth.register.pipelineMode.useSmartAgent"
                  titleDefault="Smart AI Agent"
                  descriptionId="auth.register.pipelineMode.useSmartAgentDescription"
                  descriptionDefault="Automatically chooses the best method based on document analysis.Recommended for most use cases."
                  badge={{
                    textId: "auth.register.pipelineMode.recommended",
                    textDefault: "Recommended",
                    bgColor: "bg-[#2563EB]",
                    textColor: "text-[#FFFFFF]",
                  }}
                  features={[
                    {
                      textId: "auth.register.pipelineMode.manual",
                      textDefault: "Zero manual decisions",
                    },
                    {
                      textId: "auth.register.pipelineMode.adapts",
                      textDefault: "Adapts per document",
                    },
                    {
                      textId: "auth.register.pipelineMode.consistentQuality",
                      textDefault: "Consistent quality",
                    },
                  ]}
                />
                <PipelineModeCard
                  mode="USE_TOC"
                  currentMode={pipelineMode}
                  onSelect={setPipelineMode}
                  icon={ListTree}
                  iconColor="text-[#22C55E]"
                  titleId="auth.register.pipelineMode.useToc"
                  titleDefault="Table of Contents"
                  descriptionId="auth.register.pipelineMode.useTocDescription"
                  descriptionDefault="Uses the document's table of contents to identify sections. Best for well-structured documents with clear TOC."
                  badge={{
                    textId: "auth.register.pipelineMode.fastest",
                    textDefault: "Fastest",
                    bgColor: "bg-[#DCFCE7]",
                    textColor: "text-[#22C55E]",
                  }}
                  features={[
                    {
                      textId:
                        "auth.register.pipelineMode.technicalDocumentation",
                      textDefault: "Technical documentation",
                    },
                    {
                      textId: "auth.register.pipelineMode.policyManuals",
                      textDefault: "Policy manuals",
                    },
                  ]}
                />
                <PipelineModeCard
                  mode="USE_SNOWFLAKE"
                  currentMode={pipelineMode}
                  onSelect={setPipelineMode}
                  icon={Sparkles}
                  iconColor="text-[#F97316]"
                  titleId="auth.register.pipelineMode.useSnowflake"
                  titleDefault="AI Processing Only"
                  descriptionId="auth.register.pipelineMode.useSnowflakeDescription"
                  descriptionDefault="AI analyzes content to identify logical sections. Best for documents without clear structure."
                  features={[
                    {
                      textId: "auth.register.pipelineMode.unstructured",
                      textDefault: "Unstructured documents",
                    },
                    {
                      textId: "auth.register.pipelineMode.poorFormatting",
                      textDefault: "Poor formatting",
                    },
                    {
                      textId: "auth.register.pipelineMode.legacyPdfs",
                      textDefault: "Legacy PDFs",
                    },
                  ]}
                />
              </div>
            </div>
          ) : currentStep === REGISTER_STEP.SUCCESS ? (
            <div className="flex flex-col gap-4">
              <Button asChild>
                <Link to={ROUTES.LOGIN}>
                  <FormattedMessage
                    id="auth.register.backToLogin"
                    defaultMessage="Log In"
                  />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="w-full max-w-[1400px] mx-auto px-4 flex justify-center">
              <RegisterDemoWidget
                email={email}
                name={fullname.trim()}
                onBack={() => setCurrentStep(REGISTER_STEP.PERSONAL_DETAILS)}
              />
            </div>
          )}
          {currentStep === REGISTER_STEP.PERSONAL_DETAILS ? (
            <div className="flex flex-col gap-[16px]">
              <Button>
                <FormattedMessage
                  id="auth.register.continue"
                  defaultMessage="Continue"
                />
              </Button>
              <span className="text-sm text-muted-foreground font-semibold text-center">
                <FormattedMessage id="auth.register.or" defaultMessage="or" />
              </span>
              <Button variant="secondary" asChild>
                <Link to={ROUTES.LOGIN}>
                  <FormattedMessage
                    id="auth.register.login"
                    defaultMessage="Log In"
                  />
                </Link>
              </Button>
            </div>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_NAME ? (
            <>
              <Button>
                <FormattedMessage
                  id="auth.register.continue"
                  defaultMessage="Continue"
                />
              </Button>
            </>
          ) : currentStep === REGISTER_STEP.COMPANY_DETAILS_TYPE ? (
            <>
              <Button>
                <FormattedMessage
                  id="auth.register.continue"
                  defaultMessage="Continue"
                />
              </Button>
            </>
          ) : currentStep === REGISTER_STEP.PIPELINE_MODE ? (
            <>
              <Button disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <FormattedMessage
                  id={
                    isPending
                      ? "auth.register.creatingAccount"
                      : "auth.register.createAccount"
                  }
                  defaultMessage={
                    isPending ? "Creating account..." : "Create Account"
                  }
                />
              </Button>
            </>
          ) : null}
        </form>
      </div>
    </div>
  );
};
